import React, { useContext, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from './context/AuthContext';
import axiosInstance from './axiosInstance';
import { launcherGroups } from './utils/appAccess';
import './styles/MainHomepage.css';

/* ── icon lookup (cosmetic, not stored in DB) ──────────────────── */
const ICONS = {
  SA01: '👥', SA02: '🏷️', SA03: '📡', SA04: '📻', SA05: '🎯',
  SA06: '🖥️', SA07: '🔔', SA08: '⬆️', SA09: '📊', SA10: '👤', SA11: '🆘',
  OP01: '📚', OP02: '📄', OP03: '🧪', OP04: '🔑', OP05: '📤',
  OP06: '📶', OP07: '🔐', OP08: '🎬', OP09: '📹', OP10: '📋',
  OP11: '🌐', OP12: '🔍',
};

const MainHomepage = () => {
  const { user, refreshUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [unlockMessage, setUnlockMessage] = useState('');

  /* ── fetch function definitions from API ─────────────────────── */
  const [definitions, setDefinitions] = useState([]);
  useEffect(() => {
    axiosInstance.get('/function-definitions')
      .then(({ data }) => setDefinitions(data.functions || []))
      .catch(err => console.error('Failed to load function definitions:', err));
  }, []);

  /* ── accessible apps, grouped by definition category ─────────── */
  const groups = launcherGroups(user, definitions);
  const withIcon = (app) => ({ ...app, icon: ICONS[app.code] || '🔧' });
  const saasApps = groups.saas.map(withIcon);
  const toolApps = groups.tool.map(withIcon);

  /* ── Easter egg: door unlock ─────────────────────────────────── */
  const emailPrefix = user?.email?.toLowerCase().split('@')[0];
  const isEasterEggUser = emailPrefix === 'ericolsen' || emailPrefix === 'mirkocorrales';

  const handleUnlockDoor = async () => {
    if (!isEasterEggUser) return;
    setUnlockMessage('');
    try {
      const response = await fetch('/api/unlock-door', {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        setUnlockMessage('✓ Door unlocked successfully!');
      } else {
        setUnlockMessage('✗ Failed to unlock door');
      }
    } catch (error) {
      console.error('Error unlocking door:', error);
      setUnlockMessage('✗ Error unlocking door');
    } finally {
      setTimeout(() => setUnlockMessage(''), 3000);
    }
  };

  /* ── logout ─────────────────────────────────────────────────── */
  const handleLogout = async () => {
    try {
      await axiosInstance.post('auth/logout');
      await refreshUser();
      navigate('/login');
    } catch (err) {
      console.error('Logout failed', err);
    }
  };

  /* ── get role display ───────────────────────────────────────── */
  const getRoleDisplay = () => {
    if (user?.isSuperAdmin) return { text: 'Super Admin', className: 'role-super-admin' };
    if (user?.role === 'admin') return { text: 'Admin', className: 'role-admin' };
    if (user?.role === 'user') return { text: 'User', className: 'role-user' };
    if (user?.role === 'pending') return { text: 'Pending', className: 'role-pending' };
    return { text: '', className: '' };
  };

  const roleInfo = getRoleDisplay();

  return (
    <div className="homepage">
      {/* Header */}
      <header className="homepage-header" style={{ position: 'relative' }}>
        <h1
          onClick={handleUnlockDoor}
          style={{ cursor: isEasterEggUser ? 'pointer' : 'default' }}
        >
          Solum Portal
        </h1>

        {isEasterEggUser && unlockMessage && (
          <div style={{
            position: 'absolute',
            top: '65px',
            left: '50%',
            transform: 'translateX(-50%)',
            fontSize: '13px',
            fontWeight: '500',
            color: unlockMessage.startsWith('✓') ? '#16a085' : '#e74c3c',
            background: 'rgba(0, 0, 0, 0.8)',
            padding: '8px 16px',
            borderRadius: '8px',
            whiteSpace: 'nowrap',
            zIndex: 1000
          }}>
            {unlockMessage}
          </div>
        )}

        <div className="header-right">
          <span className="user-greeting">
            Hello, <strong>{user?.displayName || 'friend'}</strong>
            {roleInfo.text && (
              <span className={`role-badge ${roleInfo.className}`}>{roleInfo.text}</span>
            )}
          </span>
        </div>
      </header>

      {/* Main Content */}
      <main className="homepage-main">
        {/* SaaS Tool Suite Section */}
        {saasApps.length > 0 && (
          <section className="app-section">
            <h2>SaaS Tool Suite</h2>
            <div className="app-grid">
              {saasApps.map(app => (
                <Link key={app.code} to={app.path} className="app-tile">
                  <span className="app-icon">{app.icon}</span>
                  <span className="app-label">{app.label}</span>
                  <span className="app-code">{app.code}</span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Solum Tool Suite Section */}
        {toolApps.length > 0 && (
          <section className="app-section">
            <h2>Solum Tool Suite</h2>
            <div className="app-grid">
              {toolApps.map(app => (
                <Link key={app.code} to={app.path} className="app-tile">
                  <span className="app-icon">{app.icon}</span>
                  <span className="app-label">{app.label}</span>
                  <span className="app-code">{app.code}</span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Admin Tools */}
        {(user?.isSuperAdmin || user?.role === 'admin') && (
          <section className="app-section">
            <h2>Admin Tools</h2>
            <div className="app-grid">
              <Link to="/redmine-portal" className="app-tile admin-tile">
                <span className="app-icon">📋</span>
                <span className="app-label">Redmine Portal</span>
              </Link>
              {user?.isSuperAdmin && (
                <Link to="/audit-logs" className="app-tile admin-tile">
                  <span className="app-icon">🔍</span>
                  <span className="app-label">Audit & Monitoring</span>
                </Link>
              )}
              <Link to="/gateway-api-docs" className="app-tile admin-tile">
                <span className="app-icon">📖</span>
                <span className="app-label">Gateway API Docs</span>
              </Link>
            </div>
          </section>
        )}

        {/* No access message */}
        {saasApps.length === 0 && toolApps.length === 0 && (
          <div className="no-access">
            <h2>No Apps Available</h2>
            <p>Your account doesn't have access to any apps yet.</p>
            <p>Please contact an administrator to request access.</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default MainHomepage;
